"use client";

import { useEffect, useRef, useState } from "react";
import { useStore } from "@/store";
import type { MediaImageCandidate } from "@/lib/media-image";
import { useSessionMediaProvider } from "@/hooks/useSessionMediaProvider";
import { withRefreshToken } from "@/hooks/webPlayerUtils";
import {
  extractMediaResponseItems,
  fetchMediaProviderJson,
  mediaProviderErrorMessage,
  normalizeMediaPlaylists,
} from "@/lib/mediaProviderClient";

export type MediaPlaylist = {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  images: MediaImageCandidate[];
  trackCount: number;
  ownerId: string | null;
  ownerName: string | null;
  collaborative: boolean;
  isPublic: boolean | null;
};

export type MediaPlaylistTrack = {
  id?: string;
  uri?: string;
  name?: string;
  artists?: Array<{ name?: string }>;
  album?: {
    name?: string | null;
    images?: Array<{ url?: string; width?: number | null; height?: number | null }>;
  };
  duration_ms?: number;
  preview_url?: string | null;
  is_restricted?: boolean;
  restriction_reason?: string | null;
  restriction_reasons?: string[];
};

export type MediaPlaylistTrackItem = {
  track?: MediaPlaylistTrack;
  id?: string;
  uri?: string;
};

function useProviderFocusRefreshKey(refreshOnWindowFocus: boolean) {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!refreshOnWindowFocus) return;

    const bump = () => {
      setRefreshKey((prev) => prev + 1);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        bump();
      }
    };

    window.addEventListener("focus", bump);
    window.addEventListener("pageshow", bump);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", bump);
      window.removeEventListener("pageshow", bump);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshOnWindowFocus]);

  return refreshKey;
}

export function useMediaPlaylists(refreshToken = 0) {
  const authStatus = useStore.use.authStatus();
  const { provider, adapter } = useSessionMediaProvider();
  const focusRefreshKey = useProviderFocusRefreshKey(
    adapter?.refreshOnWindowFocus ?? false,
  );
  const [playlists, setPlaylists] = useState<MediaPlaylist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authStatus === "checking") {
      setLoading(true);
      return;
    }

    const providerAdapter = adapter;

    if (!provider || !providerAdapter) {
      setPlaylists([]);
      setError(null);
      setLoading(false);
      return;
    }

    const activeAdapter = providerAdapter;

    async function fetchPlaylists() {
      try {
        setLoading(true);
        setError(null);
        const endpoint = withRefreshToken(
          activeAdapter.playlists.endpoint,
          refreshToken,
        );
        const json = await fetchMediaProviderJson<unknown>(
          endpoint,
          { cache: "no-store" },
          "Failed to fetch playlists",
        );
        setPlaylists(
          normalizeMediaPlaylists(
            json,
            activeAdapter.collaborativePlaylistsByDefault,
          ),
        );
      } catch (err: unknown) {
        console.error("Failed to fetch playlists", err);
        setError(mediaProviderErrorMessage(err, "Failed to fetch playlists"));
      } finally {
        setLoading(false);
      }
    }
    fetchPlaylists();
  }, [adapter, authStatus, focusRefreshKey, provider, refreshToken]);

  return { playlists, loading, error };
}

export function useMediaPlaylistTracks(
  playlistId: string | null,
  refreshToken = 0
) {
  const authStatus = useStore.use.authStatus();
  const { provider, adapter } = useSessionMediaProvider();
  const focusRefreshKey = useProviderFocusRefreshKey(
    adapter?.refreshOnWindowFocus ?? false,
  );
  const [tracks, setTracks] = useState<MediaPlaylistTrackItem[]>([]);
  const [tracksPlaylistId, setTracksPlaylistId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestScopeKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (authStatus === "checking") {
      setLoading(false);
      return;
    }

    const providerAdapter = adapter;

    if (!provider || !providerAdapter) {
      setTracks([]);
      setTracksPlaylistId(null);
      setError(null);
      setLoading(false);
      requestScopeKeyRef.current = null;
      return;
    }

    const activeAdapter = providerAdapter;

    const resolvedPlaylistId = playlistId;
    if (!resolvedPlaylistId) {
      setTracks([]);
      setTracksPlaylistId(null);
      setError(null);
      setLoading(false);
      requestScopeKeyRef.current = null;
      return;
    }

    const nextRequestScopeKey = `${provider}:${resolvedPlaylistId}`;
    const playlistChanged = requestScopeKeyRef.current !== nextRequestScopeKey;

    // Clear stale tracks immediately when the user actually changes the
    // selected playlist or provider, but keep the current list mounted during
    // same-playlist refreshes so the session UI does not blank out.
    if (playlistChanged) {
      setTracks([]);
      setTracksPlaylistId(null);
      requestScopeKeyRef.current = nextRequestScopeKey;
    }
    setError(null);
    setLoading(true);
    const controller = new AbortController();
    let cancelled = false;

    async function fetchTracks(activePlaylistId: string) {
      try {
        const endpoint = withRefreshToken(
          activeAdapter.playlists.tracksEndpoint(activePlaylistId),
          refreshToken,
        );
        const json = await fetchMediaProviderJson<unknown>(
          endpoint,
          {
            signal: controller.signal,
            cache: "no-store",
          },
          "Failed to fetch playlist tracks",
        );
        if (cancelled) return;
        setTracks(extractMediaResponseItems<MediaPlaylistTrackItem>(json));
        setTracksPlaylistId(activePlaylistId);
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        if (cancelled) return;
        console.error("Failed to fetch playlist tracks", err);
        setError(
          mediaProviderErrorMessage(err, "Failed to fetch playlist tracks"),
        );
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    }

    void fetchTracks(resolvedPlaylistId);
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [adapter, authStatus, focusRefreshKey, playlistId, provider, refreshToken]);

  const scopedTracks = tracksPlaylistId === playlistId ? tracks : [];
  return { tracks: scopedTracks, loading, error };
}
